import React, { useEffect, useState, useCallback } from "react";
import {
    Table,
    Button,
    Modal,
    Form,
    Input,
    Space,
    Typography,
    Card,
    message,
    Select,
    Popconfirm,
    Row,
    Col,
    Tag,
} from "antd";
import {
    PlusOutlined,
    ReloadOutlined,
    EditOutlined,
    DeleteOutlined,
    SearchOutlined,
} from "@ant-design/icons";
import {
    getCaseTypesApi,
    createCaseTypeApi,
    updateCaseTypeApi,
    deleteCaseTypeApi,
} from "../../../../api/caseType.api";
import { getCaseCategoriesApi } from "../../../../api/caseCategory.api";

const { Title } = Typography;
const { Option } = Select;

const CaseType = () => {
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });

    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState("");

    const [createOpen, setCreateOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [selected, setSelected] = useState(null);

    const [createForm] = Form.useForm();
    const [editForm] = Form.useForm();

    // 🔥 Fetch Case Types
    const fetchData = useCallback(async (page = 1, limit = 10, search = "") => {
        setLoading(true);
        try {
            const { data } = await getCaseTypesApi(page, limit, search);

            setItems(data.data.items || []);
            setPagination({
                current: data.data.pagination.page,
                pageSize: data.data.pagination.limit,
                total: data.data.pagination.total,
            });
        } catch {
            message.error("Failed to load case types");
        } finally {
            setLoading(false);
        }
    }, []);

    // 🔥 Fetch Categories
    const fetchCategories = async () => {
        try {
            const { data } = await getCaseCategoriesApi(1, 100);
            setCategories(data.data.items || []);
        } catch {
            message.error("Failed to load categories");
        }
    };

    useEffect(() => {
        fetchData(1, pagination.pageSize, searchText);
        fetchCategories();
    }, [fetchData]);

    // 🔥 Create
    const handleCreate = async (values) => {
        try {
            await createCaseTypeApi(values);
            message.success("Created successfully");
            setCreateOpen(false);
            createForm.resetFields();
            fetchData();
        } catch {
            message.error("Create failed");
        }
    };

    // 🔥 Update
    const handleUpdate = async (values) => {
        try {
            await updateCaseTypeApi(selected.case_type_id, values);
            message.success("Updated successfully");
            setEditOpen(false);
            editForm.resetFields();
            fetchData();
        } catch {
            message.error("Update failed");
        }
    };

    // 🔥 Delete
    const handleDelete = async (id) => {
        try {
            await deleteCaseTypeApi(id);
            message.success("Deleted successfully");
            fetchData();
        } catch {
            message.error("Delete failed");
        }
    };

    // 🔥 Columns
    const columns = [
        {
            title: "ID",
            dataIndex: "case_type_id",
            width: 80,
        },
        {
            title: "Category",
            dataIndex: "category_name",
        },
        {
            title: "Case Type",
            dataIndex: "case_type_name",
        },
        {
            title: "Action",
            render: (_, row) => (
                <Space>
                    <Button
                        icon={<EditOutlined />}
                        onClick={() => {
                            setSelected(row);
                            editForm.setFieldsValue(row);
                            setEditOpen(true);
                        }}
                    />
                    <Popconfirm
                        title="Delete this?"
                        onConfirm={() => handleDelete(row.case_type_id)}
                    >
                        <Button danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <Row justify="space-between" style={{ marginBottom: 20, padding: "10px 10px" }}>
                <Col>
                    <Title level={4}>মামলার শ্রেণী / কেস টাইপ</Title>
                    <Tag>{pagination.total} total</Tag>
                </Col>

                <Col>
                    <Space>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={() => fetchData()}
                        />
                        <Input.Search
                            placeholder="Search..."
                            onSearch={(v) => {
                                setSearchText(v);
                                fetchData(1, pagination.pageSize, v);
                            }}
                            enterButton={<SearchOutlined />}
                        />
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => setCreateOpen(true)}
                        >
                            Add
                        </Button>
                    </Space>
                </Col>
            </Row>

            <Card>
                <Table
                    columns={columns}
                    dataSource={items}
                    rowKey="case_type_id"
                    loading={loading}
                    pagination={{
                        ...pagination,
                        onChange: (page, pageSize) =>
                            fetchData(page, pageSize, searchText),
                    }}
                />
            </Card>

            {/* CREATE MODAL */}
            <Modal
                title="Add Case Type"
                open={createOpen}
                onCancel={() => setCreateOpen(false)}
                footer={null}
            >
                <Form form={createForm} onFinish={handleCreate} layout="vertical">
                    <Form.Item name="category_id" label="Category" rules={[{ required: true }]}>
                        <Select placeholder="Select category">
                            {categories.map((c) => (
                                <Option key={c.category_id} value={c.category_id}>
                                    {c.category_name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="case_type_name"
                        label="Case Type"
                        rules={[{ required: true }]}
                    >
                        <Input />
                    </Form.Item>

                    <Button type="primary" htmlType="submit" block>
                        Create
                    </Button>
                </Form>
            </Modal>

            {/* EDIT MODAL */}
            <Modal
                title="Edit Case Type"
                open={editOpen}
                onCancel={() => setEditOpen(false)}
                footer={null}
            >
                <Form form={editForm} onFinish={handleUpdate} layout="vertical">
                    <Form.Item name="category_id" label="Category">
                        <Select>
                            {categories.map((c) => (
                                <Option key={c.category_id} value={c.category_id}>
                                    {c.category_name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item name="case_type_name" label="Case Type">
                        <Input />
                    </Form.Item>

                    <Button type="primary" htmlType="submit" block>
                        Update
                    </Button>
                </Form>
            </Modal>
        </div>
    );
};


export default CaseType;